import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { deleteJSON, patchJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { Person, ProfileMembership } from '@/lib/contacts/types'

interface MembershipResponse {
  membership: ProfileMembership
}

export function CorrectMembershipDatesDialog({
  person,
  membership,
  onClose,
  onCorrected,
}: {
  person: Person
  membership: ProfileMembership
  onClose: () => void
  onCorrected: () => Promise<void>
}) {
  const [memberSinceOn, setMemberSinceOn] = React.useState(membership.member_since_on ?? '')
  const [renewalDueOn, setRenewalDueOn] = React.useState(membership.renewal_due_on ?? '')
  const correctDates = useMutation({
    mutationFn: () =>
      patchJSON<MembershipResponse>(`/api/v1/persons/${person.id}/membership/correct_dates`, {
        membership: {
          member_since_on: memberSinceOn,
          renewal_due_on: renewalDueOn.trim() ? renewalDueOn : null,
        },
      }),
    onSuccess: onCorrected,
  })
  const fieldErrors = membershipCorrectionFieldErrors(correctDates.error)
  const error = fieldErrors.memberSince || fieldErrors.renewalDue || fieldErrors.membership

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            correctDates.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>Correct membership dates</DialogTitle>
            <DialogDescription>{person.name} · Membership dates</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {error && <Alert>{error}</Alert>}
            <FormField label="Member since" htmlFor="correct-member-since">
              <Input
                id="correct-member-since"
                type="date"
                value={memberSinceOn}
                onChange={(event) => setMemberSinceOn(event.target.value)}
              />
            </FormField>
            <FormField label="Renewal due" htmlFor="correct-renewal-due">
              <Input
                id="correct-renewal-due"
                type="date"
                value={renewalDueOn}
                onChange={(event) => setRenewalDueOn(event.target.value)}
              />
            </FormField>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={correctDates.isPending}>
              {correctDates.isPending ? 'Saving...' : 'Save dates'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function RemoveMembershipDialog({
  person,
  onClose,
  onRemoved,
}: {
  person: Person
  onClose: () => void
  onRemoved: () => Promise<void>
}) {
  const removeMembership = useMutation({
    mutationFn: () => deleteJSON(`/api/v1/persons/${person.id}/membership`),
    onSuccess: onRemoved,
  })
  const fieldErrors = membershipCorrectionFieldErrors(removeMembership.error)
  const error = fieldErrors.base || fieldErrors.membership

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            removeMembership.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>Remove this membership?</DialogTitle>
            <DialogDescription>{person.name} · Membership created in error</DialogDescription>
          </DialogHeader>

          {error && (
            <div className="pb-2">
              <Alert>{error}</Alert>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={removeMembership.isPending}>
              {removeMembership.isPending ? 'Removing...' : 'Remove membership'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function membershipCorrectionFieldErrors(error: Error | null) {
  return {
    base: apiFieldMessages(error, 'base')[0],
    membership: apiFieldMessages(error, 'membership')[0],
    memberSince: apiFieldMessages(error, 'member_since_on')[0],
    renewalDue: apiFieldMessages(error, 'renewal_due_on')[0],
  }
}
